import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Flame, ArrowRight, MapPin, Clock, Award } from "lucide-react";
import { ProductCard } from "../components/ProductCard";
import api from "../lib/api";

const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/06b5109e-d3c1-4bd0-9f14-6ae825c1cf11/images/5f28f9ded57f9ccd10bd309216429889032bf05892ce0fa1a41e8a7517489aa3.png";
const TEXTURE_BG = "https://static.prod-images.emergentagent.com/jobs/06b5109e-d3c1-4bd0-9f14-6ae825c1cf11/images/70d55d34baa047b87531924509ccce11c8cb74b95ed1004e165f3d595fb208b1.png";

const CATEGORIES = [
  { key: "meats", label: "Smoked Meats", img: "https://images.unsplash.com/photo-1614231556674-f91780097f53?w=1200&q=80" },
  { key: "sausages", label: "Sausages", img: "https://images.unsplash.com/photo-1601100525441-5e88c6cbedee?w=1200&q=80" },
  { key: "ready-meals", label: "Ready Meals", img: "https://images.unsplash.com/photo-1529193591184-b1d58069ecdd?w=1200&q=80" },
  { key: "rubs", label: "Rubs", img: "https://images.unsplash.com/photo-1599909533396-15c1d2dffc9e?w=1200&q=80" },
  { key: "sauces", label: "Sauces", img: "https://images.unsplash.com/photo-1593618998160-c4d3957d4546?w=1200&q=80" },
];

const Home = () => {
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api.get("/products").then(({ data }) => {
      setFeatured(data.filter((p) => p.featured).slice(0, 6));
    }).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-ink-900 via-ink-900/80 to-ink-900/30" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink-900 via-transparent to-transparent" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 w-full grid lg:grid-cols-2 gap-12 items-center py-24">
          <div className="space-y-8 animate-fade-up">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-brand/40 bg-brand/10 text-brand text-xs font-display tracking-[0.3em]">
              <Flame size={14} className="flame-flicker" /> SMOKED ON SITE • GOSFORD NSW
            </div>
            <h1 className="font-display text-6xl sm:text-7xl lg:text-8xl text-cream leading-[0.9]">
              LIVE LIFE<br/>
              <span className="text-brand">ON THE EDGE.</span>
            </h1>
            <p className="text-[#C8C4BB] text-lg leading-relaxed max-w-xl">
              Hardwood-smoked brisket, ribs and burnt ends. Hand-built rubs and sauces. Pickup only at Gosford Showgrounds — small batch, big flavour.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/shop"
                data-testid="hero-shop-btn"
                className="inline-flex items-center gap-2 px-8 h-14 bg-brand hover:bg-brand-hover text-ink-900 font-display text-2xl tracking-widest transition-all teal-glow-hover"
              >
                SHOP THE PIT <ArrowRight size={20} strokeWidth={3} />
              </Link>
              <Link
                to="/pickup"
                data-testid="hero-pickup-btn"
                className="inline-flex items-center gap-2 px-8 h-14 border-2 border-ink-600 hover:border-brand hover:text-brand text-cream font-display text-2xl tracking-widest transition-all"
              >
                PICKUP INFO
              </Link>
            </div>
          </div>

          <div className="hidden lg:flex justify-end items-center">
            <div className="grid grid-cols-2 gap-4 max-w-md">
              {[
                { icon: <Award className="text-brand" size={28} />, t: "12 HR SMOKE", s: "Low and slow over hardwood." },
                { icon: <Flame className="text-brand" size={28} />, t: "HOUSE BLENDS", s: "4 rubs. 4 sauces. All ours." },
                { icon: <MapPin className="text-brand" size={28} />, t: "GOSFORD", s: "Showgrounds pickup only." },
                { icon: <Clock className="text-brand" size={28} />, t: "READY HOT", s: "Choose your pickup slot." },
              ].map((b) => (
                <div key={b.t} className="bg-ink-800/80 backdrop-blur border border-ink-600 p-5">
                  {b.icon}
                  <div className="font-display text-2xl text-cream mt-3 tracking-wide">{b.t}</div>
                  <div className="text-sm text-[#A19D94] mt-1">{b.s}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORIES BENTO */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-12">
          <div>
            <div className="text-brand font-display text-sm tracking-[0.4em] mb-2">/01 — THE PIT</div>
            <h2 className="font-display text-5xl sm:text-6xl text-cream leading-none">EVERYTHING SMOKED. NOTHING WASTED.</h2>
          </div>
          <Link to="/shop" className="text-brand font-display text-xl tracking-widest hover:text-brand-hover transition" data-testid="categories-shop-all">
            BROWSE ALL →
          </Link>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 auto-rows-[180px] md:auto-rows-[260px]">
          {CATEGORIES.map((c, i) => (
            <Link
              key={c.key}
              to={`/shop?cat=${c.key}`}
              data-testid={`category-tile-${c.key}`}
              className={`relative overflow-hidden group border border-ink-600 hover:border-brand transition-all ${
                i === 0 ? "col-span-2 row-span-2" : ""
              }`}
            >
              <img src={c.img} alt={c.label} className="absolute inset-0 w-full h-full object-cover grayscale-[20%] group-hover:scale-110 group-hover:grayscale-0 transition-all duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink-900 via-ink-900/40 to-transparent" />
              <div className="relative h-full flex flex-col justify-end p-5">
                <div className="font-display text-3xl md:text-4xl text-cream group-hover:text-brand transition-colors">{c.label.toUpperCase()}</div>
                <div className="text-xs text-[#A19D94] tracking-widest mt-1">SHOP →</div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
        <div className="mb-12">
          <div className="text-brand font-display text-sm tracking-[0.4em] mb-2">/02 — PITMASTER'S PICKS</div>
          <h2 className="font-display text-5xl sm:text-6xl text-cream leading-none">FAN FAVOURITES</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {featured.map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} />
          ))}
        </div>
      </section>

      {/* STORY STRIP */}
      <section
        className="relative mt-24 py-24 border-y border-ink-600"
        style={{ backgroundImage: `url(${TEXTURE_BG})`, backgroundSize: "cover" }}
      >
        <div className="absolute inset-0 bg-ink-900/85" />
        <div className="relative max-w-5xl mx-auto px-6 text-center">
          <div className="text-brand font-display text-sm tracking-[0.4em] mb-4">/03 — OUR FIRE</div>
          <h2 className="font-display text-4xl sm:text-6xl text-cream leading-tight text-balance">
            "WE DON'T DO FAST. WE DO <span className="text-brand">RIGHT.</span>"
          </h2>
          <p className="text-[#C8C4BB] mt-6 max-w-2xl mx-auto leading-relaxed">
            Born on the NSW Central Coast. Every brisket gets the time it needs — never rushed, never compromised. Just hardwood, smoke and patience.
          </p>
          <Link to="/about" className="inline-block mt-8 font-display text-xl tracking-widest text-brand hover:text-brand-hover" data-testid="home-about-link">
            READ THE STORY →
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
